import {MarkerService} from "features/apartmentViewer/ApartmentViewer/containers/MarkerService";
import {Raycaster, Scene, Vector3, WebGLRenderer} from "three";
import {MarkerModel} from "features/apartmentViewer/ApartmentViewer/models/MarkerModel";
import {Layer} from "features/apartmentViewer/ApartmentViewer/layers/Layer";
import {EventManager} from "features/apartmentViewer/ApartmentViewer/services/EventManager";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";
import {LayerType} from "features/apartmentViewer/types";
import {ApartmentViewerSettings} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerSettings";

export class MarkerLayer extends Layer {
    private readonly scene: Scene;
    private raycaster: Raycaster;
    private pickedMarker: MarkerModel | null;
    public hoveredMarker: MarkerModel | null = null;

    constructor(
        canvasService: CanvasService,
        private markerContainer: MarkerService,
        private eventManager: EventManager,
        private cameraHandler: CameraHandler,
        private renderer: WebGLRenderer,
        private settings: ApartmentViewerSettings
    ) {
        super(canvasService, LayerType.Markers);

        this.pickedMarker = null;

        this.scene = new Scene();
        this.raycaster = new Raycaster();

        this.markerContainer.getMarkers().forEach((marker: MarkerModel, index) => {
            const e = marker.model;
            e.userData.marker = marker;
            e.userData.markerId = index;
            this.scene.add(marker.model);
        })
    }

    pick() : MarkerModel | null {
        const mousePos = this.eventManager.mousePositionNormalized.clone();
        this.raycaster.setFromCamera(mousePos, this.cameraHandler.camera);

        // Get all visible children of the scene for raycasting.
        const visibleObjects = this.scene.children.filter(child => child.visible);
        const intersections = this.raycaster.intersectObjects(visibleObjects);

        if (intersections.length > 0) {
            const intersectedObject = intersections[0].object;
            if (intersectedObject.userData.marker) {
                return intersectedObject.userData.marker;
            }
        }
        return null;
    }

    isHoveringMarker() {
        return this.pick() !== null;
    }

    pickClosest(cameraPosition: Vector3, hitPoint: Vector3) {
        const visibleObjects = this.scene.children.filter(child => child.visible);


        let closestMarker = null;
        let minDistance = Infinity;

        visibleObjects.forEach(object => {
            if (
                object.position.x === cameraPosition.x &&
                object.position.y === cameraPosition.y &&
                object.position.z === cameraPosition.z
            ) {
                return;
            }

            const distance = hitPoint.distanceTo(object.position);

            if (distance < minDistance && object.userData.marker) {
                minDistance = distance;
                closestMarker = object.userData.marker;
            }
        });

        return closestMarker;
    }

    pickRaymarch(cameraPosition: Vector3, direction) {
        let closestMarker : MarkerModel | null = null;
        let minScore = Infinity;

        this.markerContainer.getMarkers().forEach(marker => {
            if (marker.isVisible) {
                const distance = cameraPosition.distanceTo(marker.position);
                if (distance > 0.1) {
                    const vectorToMarker = new Vector3().subVectors(marker.position, cameraPosition);
                    if (vectorToMarker.dot(direction) > 0) {
                        const score = 0.1 * distance + vectorToMarker.cross(direction).length();
                        if (score < minScore && marker) {
                            minScore = score;
                            closestMarker = marker;
                        }
                    }
                }
            }
        });

        return closestMarker;
    }

    // Set the picked marker, unless the site manager is loading and `force` is not true.
    setSelectedMarker(marker: MarkerModel | null, _force = false) {


        //if (!settings.loading || force) {
            this.pickedMarker = marker;
        //}
    }

    // Return the current picked marker.
    getPickedMarker() {
        return this.pickedMarker;
    }

    handleHoveringMarker = () => {
        const mousePos = this.eventManager.mousePositionNormalized.clone();
        this.raycaster.setFromCamera(mousePos, this.cameraHandler.camera);

        // Get all visible children of the scene for raycasting.
        const visibleObjects = this.scene.children.filter(child => child.visible);
        const intersections = this.raycaster.intersectObjects(visibleObjects);

        if (intersections.length > 0) {
            const intersectedObject = intersections[0].object;
            if (intersectedObject.userData.marker) {
                if(this.settings.hasActiveMarker !== true)
                    this.settings.hasActiveMarker = true;

                intersectedObject.userData.marker.isHovered = true;
            }
        } else {
            if(this.settings.hasActiveMarker !== false)
                this.settings.hasActiveMarker = false;

            this.markerContainer.getMarkers().forEach((marker, _index) => {
                marker.isHovered = false;
            });
        }
    };

    update() {

        this.markerContainer.update();
        this.renderer.setClearColor(0,0);
        this.renderer.setRenderTarget(this.renderTexture);
        this.renderer.render(this.scene, this.cameraHandler.camera);
        this.renderer.setRenderTarget(null);

    }
}
